import React from 'react';
import { formatBusinessMonth, formatDate } from '../../../utils/dateFormatter';
import { arabicPlural } from '../../../utils/alertDisplay';

/**
 * The report list. One row per owner-month, newest first.
 *
 * Three levels on one component:
 *   - Owner — month, property count, open. Nothing else exists on their rows;
 *     the backend allow-list does not send status or release state.
 *   - Ops (`isOps`) — plus the owner's name and the release state.
 *   - Report manager (`canManage`, core staff) — plus the actions. Narrower than
 *     `isOps` on purpose: seeing figures and deciding what an owner receives are
 *     different permissions, and the backend gates the writes the same way.
 *
 * No pagination by decision: twelve reports per owner per year.
 */

const StatusBadge = ({ report }) => {
  if (report.status === 'failed') {
    return <span className="report-badge is-failed">فشل الإنشاء</span>;
  }
  // "يظهر / لا يظهر" not "منشور" — nothing is sent anywhere yet, the flag only
  // controls whether the owner can see the report in the app.
  return report.released
    ? <span className="report-badge is-released">يظهر للمالك</span>
    : <span className="report-badge is-unreleased">لا يظهر للمالك</span>;
};

/**
 * WHY a build failed, not just that it did — the badge alone sent an operator to
 * the server log, which most of them cannot read.
 *
 * The reason is an untranslated engine message (an id, a stack-level string), so it
 * renders monospaced and direction-detected inside the RTL row: an Arabic-direction
 * technical string reverses its punctuation and becomes unreadable to the person
 * meant to act on it. Deliberately subordinate — the row's subject is still the month.
 *
 * `dir="auto"`, not `dir="ltr"`: this field carries whatever `error.message` the build
 * threw, and the codebase does throw Arabic messages elsewhere. Hardcoding LTR would
 * force-flip an Arabic reason's punctuation — the exact corruption this guards
 * against, inverted. `auto` behaves identically for Latin text and self-corrects.
 */
const FailureReason = ({ report }) => {
  const reason = report.status === 'failed'
    ? report.failureReason
    : report.lastFailureReason;

  if (!reason) return null;

  return (
    <p className="report-row-failure">
      <span className="report-failure-label">
        {report.status === 'failed' ? 'سبب الفشل' : 'سبب فشل المحاولة الأخيرة'}
      </span>
      <code className="report-failure-detail" dir="auto">{reason}</code>
    </p>
  );
};

const MonthlyReportsList = ({
  reports,
  isOps,
  canManage,
  onOpen,
  onRelease,
  onRegenerate,
  busyId
}) => (
  <ul className="monthly-reports-list">
    {reports.map(report => {
      const busy = busyId === report._id;

      return (
        <li key={report._id} className="monthly-reports-row">
          <button
            type="button"
            className="report-row-main"
            onClick={() => onOpen(report._id)}
            disabled={report.status === 'failed'}
          >
            <span className="report-row-month">{formatBusinessMonth(report.periodMonth)}</span>
            <span className="report-row-meta">
              {isOps && report.owner?.name ? `${report.owner.name} · ` : ''}
              {report.propertyCount}{' '}
              {arabicPlural(report.propertyCount, 'عقار', 'عقاران', 'عقارات')}
              {report.generatedAt ? ` · أُنشئ في ${formatDate(report.generatedAt)}` : ''}
            </span>
          </button>

          {isOps && (
            <div className="report-row-actions">
              <StatusBadge report={report} />

              {report.lastFailureAt && report.status === 'generated' && (
                <span className="report-badge is-warning" title="فشلت المحاولة الأخيرة لإعادة الإنشاء؛ التقرير السابق سليم">
                  فشلت المحاولة الأخيرة
                </span>
              )}

              {canManage && !report.released && report.status === 'generated' && (
                <button
                  type="button"
                  className="btn-report-action is-primary"
                  onClick={() => onRelease(report._id)}
                  disabled={busy}
                >
                  إظهار للمالك
                </button>
              )}

              {canManage && (
                <button
                  type="button"
                  className="btn-report-action"
                  onClick={() => onRegenerate(report)}
                  disabled={busy}
                >
                  إعادة الإنشاء
                </button>
              )}
            </div>
          )}

          {/* Full-width beneath the row so it never competes with the badges for
              space. Ops-only: an owner's rows carry no failure fields at all. */}
          {isOps && <FailureReason report={report} />}
        </li>
      );
    })}
  </ul>
);

export default MonthlyReportsList;
